import { useCallback, useEffect, useState } from 'react';
import { flattenDeep } from 'lodash-es';
import { useMutation } from '@tanstack/react-query';
import { CreateEventTicketDto, EventDto, UpsertEventTicketsReservationDto } from '@sweatpals/shared';
import { getReponseDataFromAxiosError } from '@sweatpals/react-ui-kit';
import { eventTicketsReservationService } from '../../services';

export interface EventTicketsReservationProps {
	eventId?: string;
	instance?: Date | string;
	eventData?: EventDto;
	tickets?: CreateEventTicketDto[];
	upsertUserEventTicketReservations(
		eventId: string,
		instance: Date | string,
		data: UpsertEventTicketsReservationDto[]
	): Promise<void>;
	refetchEventTicketsCountAndSpots(): Promise<any>;
	refetchUserEventTicketReservations(): Promise<any>;
	onError?(err: Error): void;
	onErrorReservationTimeEnded?(): void;
}

export const useEventUpsertReservations = (props: EventTicketsReservationProps): {
	eventCanReserveSpots: boolean;
	ticketIsReserved: boolean;
	reservationTimer: number | null;
	clearReservations(): Promise<void>;
	upsertReservations(): Promise<void>;
} => {
	const {
		eventId,
		instance,
		eventData,
		tickets,
		refetchEventTicketsCountAndSpots,
		refetchUserEventTicketReservations,
		onErrorReservationTimeEnded,
		onError,
	} = props;

	// check if event supports ticket reservation
  const eventCanReserveSpots = !!eventData?.ticketReservationTimeMinutes && !!eventData?.prices?.length && !!eventData?.isPaid;

	const [ticketIsReserved, setTicketIsReserved] = useState(false);
	const [reservationTimeoutId, setReservationTimeoutId] = useState<NodeJS.Timeout | number | null>(null);
	const [reservationTimer, setReservationTimer] = useState<number | null>(null);

	const callUpsertUserEventTicketReservations = useCallback(
		async (data?: UpsertEventTicketsReservationDto[]): Promise<void> => {
			if (!eventCanReserveSpots || !eventId || !instance) {
				return;
			}

			await eventTicketsReservationService.upsertUserEventTicketReservations(eventId, instance, data ?? []);

			if (data?.length) {
				setTicketIsReserved(true);
			} else {
				setTicketIsReserved(false);
			}
		},
		[eventId, instance, eventCanReserveSpots]
	);

	const clearReservations = useCallback(async () => {
		await callUpsertUserEventTicketReservations([]);
	}, [callUpsertUserEventTicketReservations]);

	const restartReservationTimer = useCallback((): void => {
		if (!eventCanReserveSpots) {
			return;
		}

		// setup reservation timer to tick each second
		const ticketReservationTimeSeconds = eventData.ticketReservationTimeMinutes * 60;
		setReservationTimer(ticketReservationTimeSeconds);

		// after reservation time ends, clear reservations
		if (reservationTimeoutId) {
			clearTimeout(reservationTimeoutId);
		}
		const ticketReservationTime = ticketReservationTimeSeconds * 1000;
		const newReservationTimeoutId = setTimeout(clearReservations, ticketReservationTime);
		setReservationTimeoutId(newReservationTimeoutId);
	}, [clearReservations, eventCanReserveSpots, eventData?.ticketReservationTimeMinutes, reservationTimeoutId]);

	const { mutateAsync } = useMutation(
		async () => {
			if (!eventCanReserveSpots) {
				return;
			}

			// clear reservations if no tickets selected
			if (!tickets?.length) {
				await clearReservations();

				return;
			}

			const data: UpsertEventTicketsReservationDto[] = flattenDeep(
				tickets.map(({ quantity, eventPriceId }) => {
					const baseEventPriceId = eventData.prices?.find(price => price.id === eventPriceId)?.basePriceId;

					return Array.from({ length: quantity }).map(() => ({
						baseEventPriceId: baseEventPriceId ?? eventPriceId,
					}));
				})
			);

			await callUpsertUserEventTicketReservations(data);
			await refetchEventTicketsCountAndSpots();
			await refetchUserEventTicketReservations();

			restartReservationTimer();
		},
		{
			onError: (err: Error) => {
				setTicketIsReserved(false);
				const data = getReponseDataFromAxiosError(err);

				if (data?.statusCode === 409) {
					onErrorReservationTimeEnded?.();
					clearReservations();

					return;
				}

				onError?.(err);
			},
		}
	);

	// tick reservation timer each second
	useEffect(() => {
		if (!ticketIsReserved || !eventCanReserveSpots) {
			return;
		}

		const interval = setInterval(() => {
			setReservationTimer(value => (value > 0 ? value - 1 : 0));
		}, 1000);

		return () => {
			setReservationTimer(null);
			clearInterval(interval);
		};
	}, [ticketIsReserved]);

	const upsertReservations = useCallback(async () => {
		await mutateAsync();
	}, [mutateAsync]);

	return {
		eventCanReserveSpots,
		upsertReservations,
		clearReservations,
		ticketIsReserved,
		reservationTimer,
	};
};
